<template>
  <div class="stock-momentum">
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1>动量指标</h1>
        <div class="title-decoration"></div>
      </div>
      <p class="page-subtitle">12日动量与5日变化率，捕捉价格动能</p>
    </div>

    <div class="filter-bar">
      <el-select
        v-model="searchForm.code"
        placeholder="请选择股票"
        filterable
        clearable
        @change="handleStockChange"
        class="stock-select"
        v-loading="stockListLoading"
      >
        <el-option
          v-for="item in stockList"
          :key="item.stock_code"
          :label="`${item.stock_name} (${item.stock_code})`"
          :value="item.stock_code"
        >
        </el-option>
      </el-select>

      <el-date-picker
        v-model="startDate"
        type="date"
        placeholder="开始日期"
        value-format="yyyy-MM-dd"
        @change="fetchData"
      />
      <span>至</span>
      <el-date-picker
        v-model="endDate"
        type="date"
        placeholder="结束日期"
        value-format="yyyy-MM-dd"
        @change="fetchData"
      />
      <el-button type="primary" @click="fetchData">查询</el-button>
    </div>

    <!-- 最新状态卡片 -->
    <div v-if="latestStatus" class="latest-status-card">
      <div class="status-header">
        <h3>{{ currentStockName || searchForm.code }} 最新状态</h3>
        <span class="status-date">{{ latestStatus.stat_date }}</span>
      </div>
      <div class="status-metrics">
        <div class="metric-card" :class="getMetricCardClass(latestStatus.trend)">
          <div class="metric-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 7L18 12H15V16H9V12H6L11 7H13Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="metric-content">
            <div class="metric-label">趋势状态</div>
            <div class="metric-value" :class="getStatusClass(latestStatus.trend)">
              {{ latestStatus.trend }}
            </div>
          </div>
        </div>

        <div class="metric-card metric-momentum">
          <div class="metric-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12H9M9 12L12 8M9 12L12 16M15 12H21M15 12L18 8M15 12L18 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="metric-content">
            <div class="metric-label">12日动量</div>
            <div class="metric-value" :class="getValueClass(parseFloat(latestStatus.momentum_12d))">
              {{ formatValue(parseFloat(latestStatus.momentum_12d)) }}%
            </div>
          </div>
        </div>

        <div class="metric-card metric-roc">
          <div class="metric-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2V6M12 18V22M6 12H2M6 12L10 8M6 12L10 16M18 12H22M18 12L14 8M18 12L14 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="metric-content">
            <div class="metric-label">5日变化率</div>
            <div class="metric-value" :class="getValueClass(parseFloat(latestStatus.roc_5d))">
              {{ formatValue(parseFloat(latestStatus.roc_5d)) }}%
            </div>
          </div>
        </div>

        <div class="metric-card metric-price">
          <div class="metric-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="metric-content">
            <div class="metric-label">收盘价</div>
            <div class="metric-value price">
              {{ parseFloat(latestStatus.close).toFixed(2) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 历史数据卡片网格 -->
    <div v-if="momentumData.length > 0" class="history-section">
      <h3 class="section-title">历史数据</h3>
      <div class="cards-grid">
        <div
          v-for="(item, index) in momentumData.slice().reverse()"
          :key="index"
          class="data-card"
          :class="getCardClass(item.trend)"
        >
          <div class="card-header">
            <span class="card-date">{{ item.stat_date }}</span>
            <el-tag :type="getTagType(item.trend)" size="small">
              {{ item.trend }}
            </el-tag>
          </div>
          <div class="card-metrics">
            <div class="card-metric">
              <span class="card-metric-label">收盘价</span>
              <span class="card-metric-value">{{ parseFloat(item.close).toFixed(2) }}</span>
            </div>
            <div class="card-metric">
              <span class="card-metric-label">12日动量</span>
              <span class="card-metric-value" :class="getValueClass(parseFloat(item.momentum_12d))">
                {{ parseFloat(item.momentum_12d).toFixed(2) }}%
              </span>
            </div>
            <div class="card-metric">
              <span class="card-metric-label">5日变化率</span>
              <span class="card-metric-value" :class="getValueClass(parseFloat(item.roc_5d))">
                {{ parseFloat(item.roc_5d).toFixed(2) }}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else-if="!loading && !latestStatus" class="empty-state">
      <el-empty description="暂无数据，请选择股票代码查询" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'StockMomentum',
  data() {
    return {
      searchForm: {
        code: ''
      },
      startDate: this.formatDate(new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)),
      endDate: this.formatDate(new Date()),
      stockList: [],
      stockListLoading: false,
      momentumData: [],
      latestStatus: null,
      loading: false,
      currentStockName: ''
    }
  },
  watch: {
    '$route.query.code': {
      immediate: true,
      handler(newCode) {
        if (newCode) {
          const selectedStock = this.stockList.find(item => item.stock_code === newCode)
          if (selectedStock) {
            this.currentStockName = selectedStock.stock_name
          }
          if (newCode !== this.searchForm.code) {
            this.searchForm.code = newCode
            this.fetchStockList()
            this.fetchDataWithCode(newCode)
          }
        }
      }
    }
  },
  mounted() {
    if (!this.$route.query.code) {
      this.searchForm.code = 'sz.002602'
      this.fetchStockList()
      this.fetchData()
    }
  },
  methods: {
    async fetchStockList() {
      this.stockListLoading = true
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success') {
          this.stockList = response.data.data
        } else {
          this.stockList = []
        }
      } catch (error) {
        console.error('获取股票列表失败:', error)
        this.$message.error('获取股票列表失败')
        this.stockList = []
      } finally {
        this.stockListLoading = false
      }
    },

    handleStockChange(code) {
      this.$router.push({ query: { code } })
    },

    async fetchDataWithCode(code) {
      this.loading = true
      try {
        const params = {
          start_date: this.startDate,
          end_date: this.endDate
        }

        const response = await axios.get(`/stock/momentum/${code}`, { params })

        if (response.data.status === 'success' && response.data.data) {
          this.momentumData = response.data.data
          if (this.momentumData.length > 0) {
            this.latestStatus = this.momentumData[this.momentumData.length - 1]
          }
        } else {
          this.$message.error('返回状态异常: ' + (response.data.message || '未知错误'))
        }
      } catch (error) {
        console.error('获取动量指标失败:', error)
        this.$message.error('获取动量指标失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },

    async fetchData() {
      this.fetchDataWithCode(this.searchForm.code)
    },

    getStatusClass(trend) {
      const classMap = {
        '强劲上升': 'status-strong-up',
        '上升': 'status-up',
        '强劲下跌': 'status-strong-down',
        '下跌': 'status-down',
        '震荡': 'status-neutral'
      }
      return classMap[trend] || 'status-neutral'
    },

    getMetricCardClass(trend) {
      const classMap = {
        '强劲上升': 'card-strong-up',
        '上升': 'card-up',
        '强劲下跌': 'card-strong-down',
        '下跌': 'card-down',
        '震荡': 'card-neutral'
      }
      return classMap[trend] || 'card-neutral'
    },

    getCardClass(trend) {
      const classMap = {
        '强劲上升': 'data-card-strong-up',
        '上升': 'data-card-up',
        '强劲下跌': 'data-card-strong-down',
        '下跌': 'data-card-down',
        '震荡': 'data-card-neutral'
      }
      return classMap[trend] || 'data-card-neutral'
    },

    getValueClass(value) {
      if (value > 0) return 'value-positive'
      if (value < 0) return 'value-negative'
      return 'value-neutral'
    },

    getTagType(trend) {
      const typeMap = {
        '强劲上升': 'success',
        '上升': 'success',
        '强劲下跌': 'danger',
        '下跌': 'danger',
        '震荡': 'info'
      }
      return typeMap[trend] || 'info'
    },

    formatValue(value) {
      if (value === null || value === undefined || isNaN(value)) return '-'
      return value.toFixed(2)
    },

    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped>
.stock-momentum {
  padding: 20px;
  min-height: calc(100vh - 104px);
  position: relative;
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.15);
}

.title-icon svg {
  width: 24px;
  height: 24px;
}

.page-title h1 {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 50%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 1px;
}

.title-decoration {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, transparent 0%, #6366f1 50%, transparent 100%);
  border-radius: 2px;
}

.page-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  position: relative;
  z-index: 1;
}

.stock-select {
  width: 280px;
}

.stock-select :deep(.el-input__inner),
.filter-bar :deep(.el-date-picker .el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.stock-select :deep(.el-input__inner):hover,
.filter-bar :deep(.el-date-picker .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4);
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15);
}

.stock-select :deep(.el-input__inner):focus,
.filter-bar :deep(.el-date-picker .el-input__inner):focus {
  border-color: #6366f1;
  box-shadow: 0 0 16px rgba(99, 102, 241, 0.25);
}

.filter-bar :deep(.el-date-picker .el-input__prefix) {
  color: rgba(99, 102, 241, 0.6);
}

.filter-bar span {
  color: #64748b;
  font-size: 14px;
}

.filter-bar :deep(.el-button--primary) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-button--primary):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.45);
}

.filter-bar :deep(.el-button--primary):active {
  transform: translateY(0);
}

/* 最新状态卡片 */
.latest-status-card {
  margin-bottom: 32px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 1;
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.status-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #cbd5e1;
}

.status-date {
  font-size: 14px;
  color: #64748b;
}

.status-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: rgba(15, 23, 42, 0.4);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
  flex-shrink: 0;
}

.metric-icon svg {
  width: 24px;
  height: 24px;
}

.metric-card.card-strong-up .metric-icon {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.metric-card.card-up .metric-icon {
  background: rgba(20, 184, 166, 0.15);
  color: #14b8a6;
}

.metric-card.card-strong-down .metric-icon {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.metric-card.card-down .metric-icon {
  background: rgba(244, 63, 94, 0.15);
  color: #f43f5e;
}

.metric-card.metric-momentum .metric-icon {
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
}

.metric-card.metric-roc .metric-icon {
  background: rgba(6, 182, 212, 0.15);
  color: #06b6d4;
}

.metric-card.metric-price .metric-icon {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.metric-content {
  flex: 1;
  min-width: 0;
}

.metric-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.metric-value {
  font-size: 20px;
  font-weight: 700;
  color: #e2e8f0;
}

.metric-value.price {
  color: #fbbf24;
}

.metric-value.value-positive {
  color: #22c55e;
}

.metric-value.value-negative {
  color: #ef4444;
}

.metric-value.value-neutral {
  color: #94a3b8;
}

/* 历史数据区域 */
.history-section {
  position: relative;
  z-index: 1;
}

.section-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #cbd5e1;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.data-card {
  padding: 16px;
  background: rgba(15, 23, 42, 0.4);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.3s ease;
  cursor: pointer;
}

.data-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  border-color: rgba(99, 102, 241, 0.3);
}

.data-card.data-card-strong-up {
  border-left: 3px solid #22c55e;
}

.data-card.data-card-up {
  border-left: 3px solid #14b8a6;
}

.data-card.data-card-strong-down {
  border-left: 3px solid #ef4444;
}

.data-card.data-card-down {
  border-left: 3px solid #f43f5e;
}

.data-card.data-card-neutral {
  border-left: 3px solid #64748b;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.card-date {
  font-size: 14px;
  font-weight: 600;
  color: #cbd5e1;
}

.card-metrics {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-metric-label {
  font-size: 12px;
  color: #64748b;
}

.card-metric-value {
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
}

.card-metric-value.value-positive {
  color: #22c55e;
}

.card-metric-value.value-negative {
  color: #ef4444;
}

.card-metric-value.value-neutral {
  color: #94a3b8;
}

/* 空状态 */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.empty-state :deep(.el-empty) {
  --el-empty-description-color: #94a3b8;
}
</style>
